import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Typography, Grid, Card, CardMedia, CardContent,
  Box, IconButton, Tooltip, Dialog, DialogActions, Button,
  Paper, Collapse, List, ListItem, ListItemButton, ListItemText,
  FormControl, InputLabel, Select, MenuItem,Stack
} from '@mui/material';
import { Favorite as FavoriteIcon, ExpandLess as ExpandLessIcon, ExpandMore as ExpandMoreIcon, DeleteSweep as DeleteSweepIcon } from '@mui/icons-material';
import instance from '../utils/axiosConfig';
import moment from 'moment';
import 'moment/locale/es';
import PriceDisplay from '../components/PriceDisplay';
import { getLeafCategories, getAllCategories, getSubcategories } from '../utils/categoryUtils';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import LoadingScreen from '../components/LoadingScreen';
import { CATEGORY_HIERARCHY } from '../constants/categories';
moment.locale('es');

const FavoriteProducts = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedMainCategory, setSelectedMainCategory] = useState('');
  const [selectedSubCategory, setSelectedSubCategory] = useState('');
  const [sortBy, setSortBy] = useState('createdAt-desc');
  const [expandedCategories, setExpandedCategories] = useState([]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [filterMenuOpen, setFilterMenuOpen] = useState(!isMobile);

  useEffect(() => {
    const fetchFavorites = async () => {
      try {
        setLoading(true);
        const response = await instance.get('/api/favorites');
        setProducts(response.data
          .map(fav => fav.product)
          .filter(product => product !== null)
        );
      } catch (error) {
        console.error('Error fetching favorites:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFavorites();
  }, []);

  const handleRemoveFavorite = async (productId) => {
    try {
      await instance.delete(`/api/favorites/${productId}`);
      setProducts(products.filter(p => p._id !== productId));
      setOpenDeleteDialog(false);
    } catch (error) {
      console.error('Error removing favorite:', error);
    }
  };

  useEffect(() => {
    let result = [...products];
    
    // Apply category filters
    if (selectedSubCategory) {
      const leafCategories = getLeafCategories(selectedMainCategory, selectedSubCategory);
      if (leafCategories.length > 0) {
        result = result.filter(product => leafCategories.includes(product.category));
      } else {
        result = result.filter(product => product.category === selectedSubCategory);
      }
    } else if (selectedMainCategory) {
      const allCategories = getAllCategories(selectedMainCategory);
      result = result.filter(product => allCategories.includes(product.category));
    }

    // Apply sorting
    const [sortField, sortOrder] = sortBy.split('-');
    result.sort((a, b) => {
      let comparison = 0;
      if (sortField === 'createdAt') {
        comparison = new Date(b.createdAt) - new Date(a.createdAt);
      } else if (sortField === 'views') {
        comparison = (b.views || 0) - (a.views || 0);
      }
      return sortOrder === 'desc' ? comparison : -comparison;
    });

    setFilteredProducts(result);
  }, [products, selectedMainCategory, selectedSubCategory, sortBy]);

  if (loading) {
    return (
      <LoadingScreen />
    );
  }

  return (
    <Container 
      maxWidth="lg" 
      sx={{ 
        py: 4,
        px: { xs: 2, sm: 4}
      }}
    >
      <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>
        Productos Favoritos
      </Typography>

      <Grid container spacing={3}>
        {/* Filters Section */}
        <Grid item xs={12} md={3}>
          {isMobile && (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'pointer'
              }}
              onClick={() => setFilterMenuOpen(!filterMenuOpen)}
              >
                <Typography variant="h6">Filtros</Typography>
                <IconButton size="small">
                  {filterMenuOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>
            </Paper>
          )}

          <Collapse in={filterMenuOpen || !isMobile}>
            <Paper sx={{ p: 2, mb: 2 }}>
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2
              }}>
                <Typography variant="h6">Filtros</Typography>
                <Button
                  size="small"
                  startIcon={<DeleteSweepIcon />}
                  onClick={() => {
                    setSelectedMainCategory('');
                    setSelectedSubCategory('');
                    setSortBy('createdAt-desc');
                    setExpandedCategories([]);
                  }}
                  sx={{
                    color: 'text.secondary',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                    fontSize: '0.875rem'
                  }}
                >
                  Limpiar
                </Button>
              </Box>

              {/* Category Filter */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>Categoría</Typography>
                <List dense>
                  {Object.entries(CATEGORY_HIERARCHY).map(([key, category]) => (
                    <React.Fragment key={key}>
                      <ListItem
                        disablePadding
                        sx={{
                          backgroundColor: selectedMainCategory === key ? 'rgba(0, 0, 0, 0.04)' : 'transparent'
                        }}
                      >
                        <ListItemButton
                          onClick={() => {
                            if (selectedMainCategory === key) {
                              setSelectedMainCategory('');
                              setSelectedSubCategory('');
                              setExpandedCategories([]);
                            } else {
                              setSelectedMainCategory(key);
                              setExpandedCategories([key]);
                            }
                          }}
                        >
                          <ListItemText
                            primary={category.name}
                            primaryTypographyProps={{
                              fontSize: '0.9rem',
                              fontWeight: selectedMainCategory === key ? 'bold' : 'normal'
                            }}
                          />
                        </ListItemButton>
                      </ListItem>

                      {(selectedMainCategory === key || expandedCategories.includes(key)) && (
                        <List component="div" dense disablePadding>
                          {getSubcategories(selectedMainCategory).map((subCategory, index) => (
                            <React.Fragment key={index}>
                              <ListItem
                                disablePadding
                                sx={{ pl: 4,
                                  backgroundColor: (selectedSubCategory === subCategory || expandedCategories.includes(subCategory)) ? 'rgba(0, 0, 0, 0.04)' : 'transparent'
                                }}
                              >
                                <ListItemButton
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (selectedSubCategory === subCategory) {
                                      setSelectedSubCategory('');
                                      setExpandedCategories([selectedMainCategory]);
                                    } else {
                                      setSelectedSubCategory(subCategory);
                                      setExpandedCategories([selectedMainCategory, subCategory]);
                                    }
                                  }}
                                >
                                  <ListItemText
                                    primary={subCategory}
                                    primaryTypographyProps={{
                                      fontSize: '0.85rem',
                                      fontWeight: (selectedSubCategory === subCategory || expandedCategories.includes(subCategory)) ? 'bold' : 'normal',
                                    }}
                                  />
                                </ListItemButton>
                              </ListItem>

                              {(selectedSubCategory === subCategory || expandedCategories.includes(subCategory)) && 
                               getLeafCategories(selectedMainCategory, subCategory).length > 0 && (
                                <ListItem>
                                  <List dense sx={{ pl: 2 }}>
                                    {getLeafCategories(selectedMainCategory, subCategory).map((leafCategory, index) => (
                                      <ListItem
                                        key={index}
                                        disablePadding
                                        sx={{ pl: 4,
                                           backgroundColor: (selectedSubCategory === leafCategory || expandedCategories.includes(leafCategory)) ? 'rgba(0, 0, 0, 0.04)' : 'transparent'
                                         }}
                                      >
                                        <ListItemButton
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedSubCategory(leafCategory);
                                            setExpandedCategories([selectedMainCategory, subCategory]);
                                          }}
                                        >
                                          <ListItemText
                                            primary={leafCategory}
                                            primaryTypographyProps={{
                                              fontSize: '0.8rem',
                                              fontWeight: (selectedSubCategory === leafCategory || expandedCategories.includes(leafCategory)) ? 'bold' : 'normal'
                                            }}
                                          />
                                        </ListItemButton>
                                      </ListItem>
                                    ))}
                                  </List>
                                </ListItem>
                              )}
                            </React.Fragment>
                          ))}
                        </List>
                      )}
                    </React.Fragment>
                  ))}
                </List>
              </Box>

              {/* Sort Options */}
              <Box sx={{ mt: 3 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Ordenar por</InputLabel>
                  <Select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    label="Ordenar por"
                  >
                    <MenuItem value="createdAt-desc">Más recientes</MenuItem>
                    <MenuItem value="createdAt-asc">Más antiguos</MenuItem>
                    <MenuItem value="views-desc">Más vistos</MenuItem>
                    <MenuItem value="views-asc">Menos vistos</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              {isMobile && (
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        width: '100%',
                        pt: 3
                      }}
                      onClick={() => setFilterMenuOpen(!filterMenuOpen)}
                    >
                    
                      <IconButton size="small">
                          <ExpandLessIcon sx={{ fontSize: 35 }} />
                         
                      </IconButton>
                    </Box>
                  )}
            </Paper>
          </Collapse>
        </Grid>

        {/* Products Grid */}
        <Grid item xs={12} md={9}>
          <Stack spacing={2}>
            {filteredProducts.map((product) => (
              <Card 
                key={product._id}
                sx={{
                  display: 'flex',
                  minHeight: {xs:'200px', md:'150px'},
                  maxHeight: {xs:'400px', md:'200px'},
                  transition: 'transform 0.2s ease-in-out',
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'scale(1.01)',
                    boxShadow: 3
                  },
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                }}
                onClick={() => navigate(`/product/${product._id}`)}
              >
                
                <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, 
                  p: 0}}>
                    
                  <CardContent sx={{ flex: '1 0 auto', py: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      
                      <Box>
                        <Typography variant="h6" gutterBottom>
                          {product.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Categoría: {CATEGORY_HIERARCHY[product.category]?.name || product.category}
                        </Typography>
                      </Box>
                      <Box>
                        <Tooltip title="Quitar de favoritos">
                          <IconButton 
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedProduct(product);
                              setOpenDeleteDialog(true);
                            }}
                          >
                            <FavoriteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>

                    <Box sx={{ mt: 'auto' }}>
                      <PriceDisplay 
                        basePrice={product.basePrice}
                        priceBreaks={product.priceBreaks}
                        hidePrice={product.hidePrice}
                        unit={product.unit}
                      />
                      <Box sx={{ mt: 1 }}>
                        {product.availableAmount && <Typography variant="caption" color="text.secondary" display="block">
                          Stock disponible: {product.availableAmount} unidades
                        </Typography>}
                        <Typography variant="caption" color="text.secondary">
                          Publicado {moment(product.createdAt).fromNow()}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Box>
              </Card>
            ))}

            {filteredProducts.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" color="text.secondary">
                  No tienes productos favoritos
                </Typography>
              </Box>
            )}
          </Stack>
        </Grid>
      </Grid>

      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancelar</Button>
          <Button 
            onClick={() => handleRemoveFavorite(selectedProduct._id)} 
            color="error" 
            autoFocus
          >
            Quitar de favoritos
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default FavoriteProducts; 